<template>
	<div v-if="form && formModelMapped">
		<el-form
			ref="dynamicFormRef"
			:model="formModel"
			label-width="200px"
			label-position="right">
			<el-form-item
				v-for="field in form"
				v-bind:key="field.name"
				v-show="field.show"
				:label="field.label ? field.label : $t(field.name)"
				:prop="field.name"
				:rules="rulesAssigner(field)">
				<component
					v-bind:is="inputTypeAssigner(field)"
					:ref="'field_' + field.name"
					v-model="formModel[field.name]"
					:options="field.options"
					:value-key="field.valueKey"
					:default-search-engine="defaultSearchEngine"
					:source-domain="formModel.sourceDomain"
					:supplier-id="formModel.supplierId"
					:purchase-type-id="formModel.purchaseTypeId"
					:offer-purchase-type-ids="offerPurchaseTypeIds"
					@isDomainSourceCorrectlyFormatted="handleDomainSourceIncorrectlyFormatted($event)"
					@isTargetUrlCorrectlyFormattedOrEmpty="handleTargetUrlIncorrectlyFormatted($event)"
					@selectedStudyKeyword="selectedStudyKeyword = $event"
					:selected-study-keyword="selectedStudyKeyword"
					:disabled="disableComponent(field)"
					@pending-supplier-change="$emit('pending-supplier-change', $event)"
					@invalid-supplier-change="$emit('invalid-supplier-change', $event)"
					@purchase-type-change="onPurchaseTypeChange($event)">
				</component>
			</el-form-item>
		</el-form>
	</div>
</template>

<script>
import RangeDatePicker from "@/pages/CampaignManager/FormItems/RangeDatePicker";
import DatePicker from "@/pages/CampaignManager/FormItems/DatePicker";
import TextArea from "@/pages/CampaignManager/FormItems/TextArea";
import Tags from "@/pages/CampaignManager/FormItems/Tags";
import Selector from "@/pages/CampaignManager/FormItems/Select";
import WatchersRenderer from "@/pages/CampaignManager/FormItems/WatchersRenderer";
import ProjectResponsiblesRenderer from "@/pages/CampaignManager/FormItems/ProjectResponsiblesRenderer";
import SourcingReasonsRenderer from "@/pages/CampaignManager/FormItems/SourcingReasonsRenderer";
import SourceDomain from "@/pages/CampaignManager/FormItems/SourceDomain";
import TargetUrl from "@/pages/CampaignManager/FormItems/TargetUrl";
import BuyingPrice from "@/pages/CampaignManager/FormItems/BuyingPrice";
import BuyingPriceCents from "@/pages/CampaignManager/FormItems/BuyingPriceCents";
import SelectStudyKeyword from "@/components/formItems/StudyKeywordsSelect";
import ContentLength from "@/components/formItems/ContentLength";
import SelectUser from "@/components/formItems/SelectUser";
import SelectAllowCreate from "@/components/formItems/SelectAllowCreate";
import SupplierSelect from "@/pages/CampaignManager/FormItems/SupplierSelect";
import BacklinkType from "@/pages/CampaignManager/FormItems/BacklinkTypeRenderer";
import { REDACTION_TYPE_ID } from "@/pages/CampaignManager/FormItems/purchaseTypes";
import { normalizeSiteSource } from "@/javascripts/normalizeSiteSource.mjs";

export default {
	components: {
		DatePicker,
		RangeDatePicker,
		TextArea,
		Tags,
		Selector,
		SourceDomain,
		TargetUrl,
		WatchersRenderer,
		ProjectResponsiblesRenderer,
		SourcingReasonsRenderer,
		BuyingPrice,
		BuyingPriceCents,
		SelectStudyKeyword,
		ContentLength,
		SelectUser,
		SelectAllowCreate,
		SupplierSelect,
		BacklinkType
	},
	props: {
		form: Array,
		value: Object,
		defaultSearchEngine: Object
	},
	data() {
		return {
			selectedStudyKeyword: null,
			backlinkPriceMargin: 0.35,
			formModelMapped: false,
			allSuppliers: [],
			// Last values this form prefilled from an offer: a field still holding one is not the
			// user's, so a later offer may replace it.
			prefilled: { buyingPrice: null, comments: null }
		};
	},
	computed: {
		// Offers of the selected supplier on the source URL without protocol, one per purchase type.
		supplierOffers() {
			const supplier = this.allSuppliers.find((candidate) => candidate.supplierId === this.formModel.supplierId);
			const sourceUrl = normalizeSiteSource(this.formModel.sourceDomain);
			if (!supplier || !sourceUrl) {
				return [];
			}
			return (supplier.links || []).filter((link) => normalizeSiteSource(link.domain) === sourceUrl);
		},
		offerPurchaseTypeIds() {
			return this.supplierOffers.map((offer) => offer.purchaseTypeId);
		},
		formModel: {
			get() {
				return this.value;
			},
			set(formModel) {
				this.$emit("input", formModel);
			}
		}
	},
	methods: {
		disableComponent(field) {
			if (field.name === "redactionPrice" && this.formModel.purchaseTypeId !== REDACTION_TYPE_ID) {
				return true;
			}
			return false;
		},
		fieldRef(name) {
			// field refs sit inside a v-for, so Vue always stores them as arrays
			// (refInFor), even though each field.name is unique.
			const refs = this.$refs["field_" + name];
			return Array.isArray(refs) ? refs[0] : refs;
		},
		rulesAssigner(field) {
			// campaign-manager needs a purchaseTypeId: a stored name that matches no purchase type
			// must be re-picked, a typed new one is created on submit.
			if (field.name === "backlinkType") {
				return [
					{
						trigger: "change",
						validator: (rule, value, callback) => {
							if (!this.formModel.purchaseTypeId && !this.fieldRef("backlinkType")?.pendingName) {
								callback(new Error(this.$t("required", { fieldName: this.$t(field.name) })));
							} else {
								callback();
							}
						}
					}
				];
			}
			if (field.name === "supplierId") {
				return [
					{
						required: true,
						trigger: "change",
						validator: (rule, value, callback) => {
							const refs = this.$refs.field_supplierId;
							const supplierField = Array.isArray(refs) ? refs[0] : refs;
							if (!value && !supplierField?.pendingSupplier) {
								callback(new Error(this.$t("required", { fieldName: this.$i18n.t(field.name) })));
							} else if (supplierField?.isSupplierDeleted) {
								callback(new Error(this.$t("deleted_supplier_error")));
							} else {
								callback();
							}
						}
					}
				];
			}
			if (field.mandatory !== undefined && field.mandatory === true) {
				var translatedName = this.$i18n.t(field.name);
				return [
					{
						required: true,
						message: this.$t("required", { fieldName: translatedName }),
						trigger: "change"
					}
				];
			} else {
				return [];
			}
		},
		inputTypeAssigner(field) {
			if (field.hasOwnProperty("fieldRenderer")) {
				return field.fieldRenderer;
			}

			const type = field.type;
			switch (type) {
				case "INTEGER":
				case "integer":
					return "el-input-number";
					break;
				case "ARRAY":
				case "array":
					return "tags";
					break;
				case "DATE":
				case "date":
					return "date-picker";
					break;
				case "RangeDate":
				case "RANGE_DATE":
				case "rangeDate":
					return "range-date-picker";
					break;
				case "STRING":
				case "string":
					return "el-input";
					break;
				case "TEXT":
				case "text":
					return "text-area";
					break;
				case "SELECT":
				case "select":
					return "selector";
					break;
				case "MULTIPLE_SELECT":
				case "multipleSelect":
					return "selector-array-objects";
				case "BOOLEAN":
				case "boolean":
					return "el-switch";
				default:
					console.log(`Type ${type} not handled. Default to el-input.`);
					return "el-input";
					break;
			}
		},
		fetchSuppliers() {
			this.$apiV2
				.get("/netlinking-sourcing/suppliers")
				.then((response) => {
					this.allSuppliers = response.data.data.attributes;
				})
				.catch((err) => console.error(err));
		},
		mapFormModel() {
			if (this.form) {
				this.form.forEach((field) => {
					field.show = true;

					if (field.name == "platform" && field.type == "select-allow-create") field.fieldRenderer = "SelectAllowCreate";

					if (field.name == "watcherIds") {
						field.fieldRenderer = "WatchersRenderer";
					}
					if (field.name == "sourceDomain") {
						field.fieldRenderer = "SourceDomain";
					}
					if (field.name == "targetUrl") {
						field.fieldRenderer = "TargetUrl";
					}
					if (field.name == "buyingPrice") {
						field.fieldRenderer = "BuyingPrice";
					}
					if (field.name == "costPerWord" || field.name == "costPerWordWithoutMargin") {
						field.fieldRenderer = "BuyingPriceCents";
					}
					if (field.name == "keyword" && field.type == "OBJECT") {
						field.fieldRenderer = "SelectStudyKeyword";
					}
					if (field.name == "contentLength") {
						field.fieldRenderer = "ContentLength";
					}
					if (field.name == "projectResponsibleIds") {
						field.fieldRenderer = "ProjectResponsiblesRenderer";
					}
					if (field.name == "sourcingReasons") {
						field.fieldRenderer = "SourcingReasonsRenderer";
					}
					if (field.name == "supplierId") {
						field.fieldRenderer = "SupplierSelect";
					}
					if (field.name == "backlinksIds" || field.name == "contentsIds") {
						field.show = false;
					}
					if (field.name == "backlinkType") {
						field.fieldRenderer = "BacklinkType";
						// Not a server field: filled by the selector, sent along with backlinkType.
						this.$set(this.formModel, "purchaseTypeId", null);
					}
					this.$set(this.formModel, field.name, field.value, field.show, field.objectValue);
				});
				// el-dialog's destroy-on-close keeps this component alive between openings: a new
				// form must not inherit the previous one's prefills, suppliers or purchase types.
				this.prefilled = { buyingPrice: null, comments: null };
				if (this.form.some((field) => field.name === "supplierId")) {
					this.fetchSuppliers();
				}
				this.fieldRef("backlinkType")?.fetchPurchaseTypes();
			}
			this.formModelMapped = true;
		},
		handleDomainSourceIncorrectlyFormatted(isBadUrl) {
			this.$emit("isDomainSourceCorrectlyFormatted", isBadUrl);
		},
		handleTargetUrlIncorrectlyFormatted(isBadUrl) {
			this.$emit("isTargetUrlCorrectlyFormattedOrEmpty", isBadUrl);
		},
		// Prefills the buying price and the comments from the offer of the selected supplier, source
		// URL without protocol and purchase type. A field is only written while it is empty or still holds
		// what this form prefilled: a user's value, or an edited backlink's stored one, is kept.
		// An offer without price or comment clears nothing.
		prefillFromOffer() {
			const offer = this.supplierOffers.find((candidate) => candidate.purchaseTypeId === this.formModel.purchaseTypeId);
			if (!offer) {
				return;
			}
			const updatedModel = { ...this.formModel };
			const isFree = (field, isEmpty) => isEmpty || updatedModel[field] === this.prefilled[field];
			let changed = false;

			if (offer.price != null && isFree("buyingPrice", !updatedModel.buyingPrice)) {
				updatedModel.buyingPrice = this.prefilled.buyingPrice = offer.price;
				changed = true;
			}
			const comment = (offer.linkComments || "").trim();
			const hasComments = this.form.some((field) => field.name === "comments");
			if (comment && hasComments && isFree("comments", !(updatedModel.comments || "").trim())) {
				updatedModel.comments = this.prefilled.comments = comment;
				changed = true;
			}
			// A single emit: a second one would start from the model before the first round trip
			// and write the previous price back.
			if (changed) {
				this.$emit("input", updatedModel);
			}
		},
		// Only a type picked by the user prefills: the id resolved from the stored name when the
		// edit form opens must not touch the backlink.
		onPurchaseTypeChange({ purchaseTypeId, userSelected }) {
			this.$set(this.formModel, "purchaseTypeId", purchaseTypeId);
			if (userSelected) {
				this.prefillFromOffer();
			}
		},
		// Lets fields resolve their deferred state (the pending intermediary supplier and
		// purchase type quick-creates) before the parent form actually submits.
		beforeSubmit() {
			const current = this.formModel?.sourceDomain;
			if (typeof current === "string") {
				const normalized = normalizeSiteSource(current);
				if (normalized !== current) {
					this.$set(this.formModel, "sourceDomain", normalized);
				}
			}
			const supplierField = this.fieldRef("supplierId");
			const purchaseTypeField = this.fieldRef("backlinkType");
			return Promise.resolve(supplierField?.commitPendingSupplier?.()).then(() => purchaseTypeField?.commitPendingPurchaseType?.());
		}
	},
	mounted() {
		this.mapFormModel();
	},
	watch: {
		form() {
			this.mapFormModel();
		},
		// when supplier Id is changed
		"value.supplierId": {
			handler(newSupplierId, oldSupplierId) {
				if (newSupplierId && newSupplierId !== oldSupplierId) {
					this.prefillFromOffer();
				}
			}
		}
	}
};
</script>

<i18n>
{
  "en": {
    "name": "Name",
    "companyName": "Company Name",
    "activitySector": "Theme",
    "companyDescription": "Company Description",
    "highlights": "Values to highlight",
    "links": "Additional Links (image, vidéo, etc)",
    "targetUrl": "Target Page",
    "platform": "Platform",
    "sourceUrl": "Url Source",
    "sourceDomain": "Domain Source",
    "price": "Price",
    "redactionPrice": "Redaction price",
    "buyingPrice": "Purchasing price",
    "backlinkType": "Type of purchase",
    "status": "Status",
    "keyword": "Keyword",
    "anchors": "Anchors",
    "numberOfWords": "Number of words",
    "comments": "Comments",
    "backlinkExample": "Example Of Backlinks",
    "articleType": "Article Type",
    "anchorExamples": "Anchor Examples",
    "budget": "Budget",
    "description": "Description",
    "period": "Period",
    "publishedDate": "Publication Date",
    "additionalLinks": "Additional links",
    "validationDate": "Validation date",
    "concept": "Concept",
    "catalog": "Catalog",
    "budgetWithoutMargin": "Budget without margin",
    "backlinkTypes": "Backlink types",
    "watcherIds": "Followed by",
    "contentLength": "Content length (words)",
    "costPerWord": "Cost/Word",
    "pricing": "Pricing",
    "costPerWordWithoutMargin": "Cost/Word without margin",
    "pageType": "Type of page",
    "writingType": "Type of writing",
    "url": "URL of existing text",
    "sourceLanguage": "Source Language",
    "targetLanguage": "Targeted Language",
    "existingTextUrl": "URL du texte existant",
    "secondaryKeywordAnchor": "Anchor of secondary keyword",
    "secondaryKeywordUrl": "URL of secondary keywor",
    "outgoingLinks": "Outgoing links",
    "sameTopicUrl": "Example of article",
    "projectResponsibleIds": "Followed by",
    "contentsIds": "Contents",
    "backlinksIds": "Backlinks",
    "publishDate": "Publish date",
    "deliveryDate": "Delivery date",
    "dateStatusDomainToValidate": "Month",
    "isTest": "Test campaign",
    "creationDate": "Month",
    "required": "The field {fieldName} is mandatory",
    "deleted_supplier_error": "This supplier no longer exists, please select another one",
    "productSiteService": "Product / Site service",
    "targetExample": "Exemple de cible",
    "contentObjective": "Objectif du contenu",
    "contentType": "Type du contenu",
    "contentSubject": "Thème / sujet du contenu",
    "subjectsCoveredByContent": "Sujets couverts par le contenu",
    "typographyExample": "Exemple de typographie",
    "toneExample": "Exemple de ton",
    "dontMentionLegalSensitivity": "Ne pas mentionner / Sensibilité juridique",
    "globalGuidelines": "Guidelines globales",
    "valueProposition": "Proposition de valeur",
    "sourcingReasons": "Sourcing Reasons",
    "supplierId": "Supplier"
  },
  "fr": {
    "name": "Nom",
    "companyName": "Nom de l'entreprise",
    "activitySector": "Thématique",
    "companyDescription": "Description de l'entreprise",
    "highlights": "Proposition de valeurs",
    "links": "Liens complémentaires (image, vidéo, etc)",
    "targetUrl": "Page visée",
    "platform": "Plateforme",
    "sourceUrl": "Url source",
    "sourceDomain": "Domaine source",
    "price": "Prix",
    "redactionPrice": "Prix de rédaction",
    "buyingPrice": "Prix d'achat",
    "backlinkType": "Type d'achat",
    "status": "Statut",
    "keyword": "Mot clé",
    "anchors": "Ancres",
    "numberOfWords": "Nombre de mots total",
    "comments": "Commentaires",
    "backlinkExample": "Exemple de Backlink",
    "articleType": "Type d'article",
    "anchorExamples": "Exemple d'ancre",
    "budget": "Budget",
    "description": "Description",
    "period": "Période",
    "publishedDate": "Date de publication",
    "additionalLinks": "Liens additionnels",
    "validationDate": "Date de validation",
    "concept": "Concept",
    "catalog": "Catalogue",
    "budgetWithoutMargin": "Budget sans les marges",
    "backlinkTypes": "Types de backlinks",
    "watcherIds": "Suivi par",
    "contentLength": "Taille du contenu (mots)",
    "costPerWord": "Prix/Mot",
    "pricing": "Tarification",
    "costPerWordWithoutMargin": "Prix/Mot sans marge",
    "pageType": "Type de page",
    "writingType": "Type de rédaction",
    "url": "URL du texte existant",
    "sourceLanguage": "Langue source",
    "targetLanguage": "Langue ciblé",
    "existingTextUrl": "URL du texte existant",
    "secondaryKeywordAnchor": "Ancre du mot clé secondaire",
    "secondaryKeywordUrl": "Url du mot lé secondaire",
    "outgoingLinks": "Liens sortants",
    "sameTopicUrl": "Exemple d'article",
    "projectResponsibleIds": "Responsables du projet",
    "contentsIds": "Contenus",
    "backlinksIds": "Backlinks",
    "publishDate": "Date de publication",
    "deliveryDate": "Date de livraison",
    "dateStatusDomainToValidate": "Mois",
    "isTest": "Campagne test",
    "creationDate": "Mois",
    "required": "Le champs {fieldName} est obligatoire",
    "deleted_supplier_error": "Ce fournisseur n'existe plus, veuillez en sélectionner un autre",
    "productSiteService": "Product / Site service",
    "targetExample": "Target example",
    "contentObjective": "Content objective",
    "contentType": "Content type",
    "contentSubject": "Content subject",
    "subjectsCoveredByContent": "Subjects covered by content",
    "typographyExample": "Typography example",
    "toneExample": "Tone example",
    "dontMentionLegalSensitivity": "Don't mention / Legal Sensitivity",
    "globalGuidelines": "Global guidelines",
    "valueProposition": "Value Proposition",
    "sourcingReasons": "Raison du sourcing",
    "supplierId": "Fournisseur"
  },
  "de": {
    "name": "Name",
    "companyName": "Name der Firma",
    "activitySector": "Thematisch",
    "companyDescription": "Firmen Beschreibung",
    "highlights": "Wertversprechen",
    "links": "Weiterführende Links (Bild, Video etc.)",
    "targetUrl": "Zielseite",
    "platform": "Plattform",
    "sourceUrl": "URL-Quelle",
    "sourceDomain": "Domain-Quelle",
    "price": "Preis",
    "redactionPrice": "Schreibpreis",
    "buyingPrice": "Kaufpreis",
    "backlinkType": "Art des Kaufs",
    "status": "Status",
    "keyword": "Stichwort",
    "anchors": "Anker",
    "numberOfWords": "Gesamtzahl der Wörter",
    "comments": "Kommentare",
    "backlinkExample": "Beispiel für Backlinks",
    "articleType": "Geben Sie d'Artikel ein",
    "anchorExamples": "Beispiel Anker",
    "budget": "Budget",
    "description": "Beschreibung",
    "period": "Zeitraum",
    "publishedDate": "Veröffentlichungsdatum",
    "additionalLinks": "Zusätzliche Links",
    "validationDate": "Validierungsdatum",
    "concept": "Konzept",
    "catalog": "Katalog",
    "budgetWithoutMargin": "Budget ohne Margen",
    "backlinkTypes": "Arten von Backlinks",
    "watcherIds": "gefolgt von",
    "contentLength": "Inhaltsgröße (Wörter)",
    "costPerWord": "Preis/Wort",
    "pricing": "Preisgestaltung",
    "costPerWordWithoutMargin": "Preis/Wort ohne Marge",
    "pageType": "Seitentyp",
    "writingType": "Art des Schreibens",
    "url": "URL du texte existant",
    "sourceLanguage": "Ausgangssprache",
    "targetLanguage": "Zielsprache",
    "existingTextUrl": "URL du texte existant",
    "secondaryKeywordAnchor": "Sekundärer Keyword-Anker",
    "secondaryKeywordUrl": "Sekundäre Keyword-URL",
    "outgoingLinks": "Ausgehende Links",
    "sameTopicUrl": "Beispielartikel",
    "projectResponsibleIds": "Projektmanager",
    "contentsIds": "Inhalt",
    "backlinksIds": "Backlinks",
    "publishDate": "Veröffentlichungsdatum",
    "deliveryDate": "Liefertermin",
    "dateStatusDomainToValidate": "Monat",
    "isTest": "Testkampagne",
    "creationDate": "Monat",
    "required": "Das Feld {fieldName} ist obligatorisch",
    "deleted_supplier_error": "Dieser Anbieter existiert nicht mehr, bitte wählen Sie einen anderen aus",
    "productSiteService": "Produkt-/Site-Service",
    "targetExample": "Zielbeispiel",
    "contentObjective": "Inhaltliches Ziel",
    "contentType": "Inhaltstyp",
    "contentSubject": "Inhaltliches Thema",
    "subjectsCoveredByContent": "Inhaltlich abgedeckte Themen",
    "typographyExample": "Typografisches Beispiel",
    "toneExample": "Tonbeispiel",
    "dontMentionLegalSensitivity": "Nicht erwähnen / Legal Sensitivity",
    "globalGuidelines": "Globale Richtlinien",
    "valueProposition": "Wertversprechen",
    "sourcingReasons": "Grund für die Beschaffung",
    "supplierId": "Lieferant"
  }
}
</i18n>
